import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  SelectField,
  TextAreaField,
  TextField,
  useToast,
  type Tone,
} from '@cartly/ui';
import { MessageCircle, Send } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { api } from '../../api/client';
import { formatRelative } from '../../app/format';
import { useLoader, useSubmit } from '../../app/hooks';

export interface SendablePhone {
  id: string;
  label: string;
  registeredAt: string | null;
  /** Used on the WhatsApp Business app too (coexistence): already registered. */
  coexistence: boolean;
}

export interface SendableTemplate {
  name: string;
  language: string;
  status: string;
  bodyParams: number;
  /** Dynamic URL button (`https://shop/{{1}}`): its position and the fixed URL prefix. */
  urlButton?: { index: number; baseUrl: string } | null;
}

interface MessageRow {
  id: string;
  contactWaId: string;
  contactName: string | null;
  direction: 'inbound' | 'outbound';
  type: string;
  body: string | null;
  templateName: string | null;
  status: string;
  errorTitle: string | null;
  sentAt: string;
}

interface ConsentRow {
  contactWaId: string;
  status: 'opted_in' | 'opted_out';
  source: string;
  updatedAt: string;
}

const STATUS_TONE: Record<string, Tone> = {
  queued: 'neutral',
  sending: 'neutral',
  received: 'info',
  sent: 'neutral',
  delivered: 'success',
  read: 'success',
  failed: 'critical',
};

/** `{{1}}`-style placeholders in a template body. */
export function countBodyParams(components: unknown): number {
  if (!Array.isArray(components)) return 0;
  const body = components.find((c) => String(c?.type).toUpperCase() === 'BODY') as
    { text?: string } | undefined;
  return new Set((body?.text ?? '').match(/\{\{\s*\d+\s*\}\}/g) ?? []).size;
}

/** The template's dynamic URL button (variable only at the end of the URL), if any. */
export function dynamicUrlButton(components: unknown): { index: number; baseUrl: string } | null {
  if (!Array.isArray(components)) return null;
  for (const c of components) {
    if (String(c?.type).toUpperCase() !== 'BUTTONS' || !Array.isArray(c.buttons)) continue;
    for (let i = 0; i < c.buttons.length; i++) {
      const b = c.buttons[i] as { type?: string; url?: string };
      if (String(b.type).toUpperCase() === 'URL' && b.url?.endsWith('{{1}}')) {
        return { index: i, baseUrl: b.url.slice(0, -'{{1}}'.length) };
      }
    }
  }
  return null;
}

function SendForm({
  phones,
  templates,
  onSent,
}: {
  phones: SendablePhone[];
  templates: SendableTemplate[];
  onSent: () => void;
}) {
  const toast = useToast();
  const submit = useSubmit();
  const approved = templates.filter((t) => t.status === 'APPROVED');
  const [phoneId, setPhoneId] = useState(phones[0]?.id ?? '');
  const [to, setTo] = useState('');
  const [mode, setMode] = useState<'template' | 'text'>('template');
  const [templateKey, setTemplateKey] = useState('');
  const [params, setParams] = useState<string[]>([]);
  const [buttonValue, setButtonValue] = useState('');
  const [text, setText] = useState('');

  const selected = approved.find((t) => `${t.name}|${t.language}` === templateKey) ?? approved[0];
  const paramCount = selected?.bodyParams ?? 0;
  const urlButton = selected?.urlButton ?? null;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const content =
      mode === 'text'
        ? { type: 'text', body: text }
        : {
            type: 'template',
            name: selected?.name,
            language: selected?.language,
            ...(paramCount > 0 || urlButton
              ? {
                  components: [
                    ...(paramCount > 0
                      ? [
                          {
                            type: 'body',
                            parameters: Array.from({ length: paramCount }, (_, i) => ({
                              type: 'text',
                              text: params[i] ?? '',
                            })),
                          },
                        ]
                      : []),
                    ...(urlButton
                      ? [
                          {
                            type: 'button',
                            sub_type: 'url',
                            index: String(urlButton.index),
                            parameters: [{ type: 'text', text: buttonValue.trim() }],
                          },
                        ]
                      : []),
                  ],
                }
              : {}),
          };
    const ok = await submit.run(async () => {
      await api.post('/whatsapp/messages', {
        phoneNumberId: phoneId,
        to: to.trim(),
        idempotencyKey: crypto.randomUUID(),
        content,
      });
      return true;
    });
    if (ok) {
      toast.success('Message queued');
      setText('');
      onSent();
    }
  };

  return (
    <form className="stack" onSubmit={(e) => void onSubmit(e)}>
      {submit.error && <Alert tone="critical">{submit.error}</Alert>}
      <SelectField
        label="Send from"
        value={phoneId}
        onChange={(e) => setPhoneId(e.target.value)}
        options={phones.map((p) => ({
          value: p.id,
          label: p.registeredAt || p.coexistence ? p.label : `${p.label} (not registered here)`,
        }))}
      />
      <TextField
        label="Customer number"
        value={to}
        onChange={(e) => setTo(e.target.value)}
        hint="International format with country code, e.g. 919876543210."
        inputMode="tel"
        autoComplete="off"
        required
      />
      <SelectField
        label="Message type"
        value={mode}
        onChange={(e) => setMode(e.target.value as 'template' | 'text')}
        options={[
          { value: 'template', label: 'Approved template (needs the customer’s opt-in)' },
          { value: 'text', label: 'Free text (only within 24 h of their last message)' },
        ]}
      />
      {mode === 'template' ? (
        approved.length === 0 ? (
          <Alert tone="info">
            No approved templates yet. Sync templates above once Meta has approved one.
          </Alert>
        ) : (
          <>
            <SelectField
              label="Template"
              value={selected ? `${selected.name}|${selected.language}` : ''}
              onChange={(e) => {
                setTemplateKey(e.target.value);
                setParams([]);
                setButtonValue('');
              }}
              options={approved.map((t) => ({
                value: `${t.name}|${t.language}`,
                label: `${t.name} (${t.language})`,
              }))}
            />
            {Array.from({ length: paramCount }, (_, i) => (
              <TextField
                key={i}
                label={`Parameter {{${i + 1}}}`}
                value={params[i] ?? ''}
                onChange={(e) => {
                  const next = [...params];
                  next[i] = e.target.value;
                  setParams(next);
                }}
                required
              />
            ))}
            {urlButton && (
              <TextField
                label="Button link value"
                value={buttonValue}
                onChange={(e) => setButtonValue(e.target.value)}
                hint={`Added after ${urlButton.baseUrl} when the customer taps the button.`}
                required
              />
            )}
          </>
        )
      ) : (
        <TextAreaField
          label="Message"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={4096}
          required
        />
      )}
      <div>
        <Button
          type="submit"
          variant="primary"
          icon={<Send size={14} />}
          loading={submit.pending}
          disabled={!phoneId || (mode === 'template' && !selected)}
        >
          Send
        </Button>
      </div>
    </form>
  );
}

function ConsentForm({ onSaved }: { onSaved: () => void }) {
  const toast = useToast();
  const submit = useSubmit();
  const [to, setTo] = useState('');
  const [status, setStatus] = useState<'opted_in' | 'opted_out'>('opted_in');
  const [note, setNote] = useState('');

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await submit.run(async () => {
      await api.put('/whatsapp/consents', {
        to: to.trim(),
        status,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      return true;
    });
    if (ok) {
      toast.success('Consent recorded');
      setTo('');
      setNote('');
      onSaved();
    }
  };

  return (
    <form className="stack" onSubmit={(e) => void onSubmit(e)}>
      {submit.error && <Alert tone="critical">{submit.error}</Alert>}
      <TextField
        label="Customer number"
        value={to}
        onChange={(e) => setTo(e.target.value)}
        inputMode="tel"
        autoComplete="off"
        required
      />
      <SelectField
        label="Consent"
        value={status}
        onChange={(e) => setStatus(e.target.value as 'opted_in' | 'opted_out')}
        options={[
          { value: 'opted_in', label: 'Opted in' },
          { value: 'opted_out', label: 'Opted out' },
        ]}
      />
      <TextField
        label="Note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        hint="Where this consent came from, e.g. “checkout checkbox, order #1001”."
        maxLength={255}
      />
      <div>
        <Button type="submit" variant="secondary" loading={submit.pending}>
          Save consent
        </Button>
      </div>
    </form>
  );
}

function RegisterForm({ phone, onDone }: { phone: SendablePhone; onDone: () => void }) {
  const toast = useToast();
  const submit = useSubmit();
  const [pin, setPin] = useState('');
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await submit.run(async () => {
      await api.post(`/whatsapp/phone-numbers/${phone.id}/register`, { pin });
      return true;
    });
    if (ok) {
      setPin('');
      toast.success('Phone number registered');
      onDone();
    }
  };
  return (
    <form className="row" onSubmit={(e) => void onSubmit(e)}>
      <TextField
        label={`PIN for ${phone.label}`}
        type="password"
        value={pin}
        onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
        error={submit.error ?? undefined}
        inputMode="numeric"
        autoComplete="off"
        required
      />
      <Button
        type="submit"
        variant="secondary"
        loading={submit.pending}
        disabled={pin.length !== 6}
      >
        {phone.registeredAt ? 'Register again' : 'Register'}
      </Button>
    </form>
  );
}

/** Sending, consent, recent messages and number registration. */
export function MessagingSections({
  phones,
  templates,
  canSend,
  canReadMessages,
  canReadConsent,
  canManageConsent,
  canManageChannels,
  onPhonesChanged,
  tenantId,
}: {
  phones: SendablePhone[];
  templates: SendableTemplate[];
  canSend: boolean;
  canReadMessages: boolean;
  canReadConsent: boolean;
  canManageConsent: boolean;
  canManageChannels: boolean;
  onPhonesChanged: () => void;
  tenantId: string | undefined;
}) {
  const messages = useLoader(
    () =>
      canReadMessages
        ? api.get<{ messages: MessageRow[] }>('/whatsapp/messages?limit=30').then((r) => r.messages)
        : Promise.resolve([] as MessageRow[]),
    tenantId,
  );
  const consents = useLoader(
    () =>
      canReadConsent
        ? api.get<{ consents: ConsentRow[] }>('/whatsapp/consents').then((r) => r.consents)
        : Promise.resolve([] as ConsentRow[]),
    tenantId,
  );
  const toast = useToast();

  if (phones.length === 0) return null;
  return (
    <>
      {canSend && (
        <Card
          title="Send a message"
          subtitle="Template messages need the customer's recorded opt-in. Free text is only allowed within 24 hours of their last message."
        >
          <SendForm
            phones={phones}
            templates={templates}
            onSent={() => {
              void messages.reload();
            }}
          />
        </Card>
      )}

      {canReadMessages && (
        <Card
          title="Recent messages"
          subtitle="Incoming and outgoing, newest first. Status updates arrive by webhook."
          flush
        >
          {!messages.data || messages.data.length === 0 ? (
            <EmptyState icon={<MessageCircle size={22} />} title="No messages yet">
              Incoming customer messages and the ones you send appear here.
            </EmptyState>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Message</th>
                    <th>Status</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {messages.data.map((m) => (
                    <tr key={m.id}>
                      <td>
                        <div className="table__primary mono">{m.contactWaId}</div>
                        {m.contactName && <div className="table__secondary">{m.contactName}</div>}
                      </td>
                      <td>
                        <div className="table__secondary">
                          {m.direction === 'inbound' ? 'Received' : 'Sent'} · {m.type}
                        </div>
                        <div>{m.templateName ?? m.body ?? '—'}</div>
                      </td>
                      <td>
                        <Badge tone={STATUS_TONE[m.status] ?? 'neutral'}>{m.status}</Badge>
                        {m.errorTitle && <div className="table__secondary">{m.errorTitle}</div>}
                      </td>
                      <td>{formatRelative(m.sentAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {(canReadConsent || canManageConsent) && (
        <Card
          title="Customer consent"
          subtitle="Customers can also reply STOP or START themselves. Opted-out customers are never messaged."
        >
          <div className="stack">
            {canManageConsent && (
              <ConsentForm
                onSaved={() => {
                  void consents.reload();
                }}
              />
            )}
            {consents.data && consents.data.length > 0 && (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Consent</th>
                      <th>Source</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consents.data.map((c) => (
                      <tr key={c.contactWaId}>
                        <td className="mono">{c.contactWaId}</td>
                        <td>
                          <Badge tone={c.status === 'opted_in' ? 'success' : 'critical'}>
                            {c.status === 'opted_in' ? 'opted in' : 'opted out'}
                          </Badge>
                        </td>
                        <td>{c.source.replace(/_/g, ' ')}</td>
                        <td>{formatRelative(c.updatedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>
      )}

      {canManageChannels && (
        <Card
          title="Register a phone number"
          subtitle="Only needed for numbers added by hand: sets the six-digit two-step verification PIN with Meta. Numbers also used on the WhatsApp Business app (coexistence) are already registered and are not listed. The PIN is sent to Meta and never stored."
        >
          <div className="stack">
            {phones
              .filter((p) => !p.coexistence)
              .map((p) => (
                <RegisterForm
                  key={p.id}
                  phone={p}
                  onDone={() => {
                    toast.success('Registered with Meta');
                    onPhonesChanged();
                  }}
                />
              ))}
          </div>
        </Card>
      )}
    </>
  );
}
